import {Week} from "../types";
import {parseRawAmount} from "./hourPrecision";

export const WEEKDAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

export interface EditableRow {
    jiraId: string;
    summary: string;
    comment?: string;
    hours: number[];              // 7 entries
    approvedDays: boolean[];      // 7 entries
    bookedDays?: boolean[];       // 7 entries (has any db_booking_id)
    overtimeDays?: boolean[];      // 7 entries (performance incentive/overtime booking)
    locked: boolean;               // Copy mode: row has at least one locked day
    lockedDays: boolean[];         // Copy mode: per-day lock (existing target booking)
    sourceExistsInTarget?: boolean;
    fromApi: boolean;              // false = added via "+ Add favorite", true = existing booking template
    blocked?: boolean;             // restricted absence code the current user may not use
}

/** True when a status string represents an approved (locked) booking. Mirrors script's isApprovedStatus. */
export function isApprovedStatus(status: string | undefined | null): boolean {
    const raw = String(status || "").toLowerCase();
    if (!raw) { return false; }
    return raw.includes("approved") && !raw.includes("not-approved");
}

/** True when a status string represents a performance incentive/overtime booking. */
export function isOvertimeStatus(status: string | undefined | null): boolean {
    return String(status || "").toLowerCase() === "overtime";
}

/**
 * Collapses a Week's bookings into one row per JIRA id with 7-day hour arrays.
 * Rows with no positive hours are skipped unless `includeBookedZeroHourRows` and
 * the row still has an existing (now zeroed) booking id for some day.
 */
export function mapWeekRows(week: Week | null | undefined, includeBookedZeroHourRows = false): Map<string, EditableRow> {
    const map = new Map<string, EditableRow>();
    const bookings = week && week.bookings ? week.bookings : {};

    for (const jiraId of Object.keys(bookings)) {
        const booking = bookings[jiraId] || ({} as Week["bookings"][string]);
        const sourceHours = Array.isArray(booking.story_booking_hours) ? booking.story_booking_hours : [];
        const sourceStatus = Array.isArray(booking.status) ? booking.status : [];
        const sourceBookingIds = Array.isArray(booking.db_booking_ids) ? booking.db_booking_ids : [];
        const hours: number[] = [];
        const approvedDays: boolean[] = [];
        const bookedDays: boolean[] = [];
        const overtimeDays: boolean[] = [];
        let hasAnyHours = false;
        let hasAnyBookings = false;

        for (let i = 0; i < 7; i += 1) {
            const value = parseRawAmount(sourceHours[i]);
            const bookingIds = String(sourceBookingIds[i] || "").split("+").map((id) => id.trim()).filter(Boolean);
            hours.push(value);
            approvedDays.push(isApprovedStatus(sourceStatus[i]));
            bookedDays.push(bookingIds.length > 0);
            overtimeDays.push(isOvertimeStatus(sourceStatus[i]));
            if (value > 0) { hasAnyHours = true; }
            if (bookingIds.length > 0) { hasAnyBookings = true; }
        }

        if (hasAnyHours || (includeBookedZeroHourRows && hasAnyBookings)) {
            map.set(jiraId, {
                jiraId,
                summary: booking.story_name || "",
                hours,
                approvedDays,
                bookedDays,
                overtimeDays,
                locked: false,
                lockedDays: [false, false, false, false, false, false, false],
                fromApi: true
            });
        }
    }

    return map;
}

export interface BuildCopyRowsResult {
    rows: EditableRow[];
    blockedJiraIds: string[];
}

/**
 * Builds the editable row set for Copy Week: source-week rows pre-filled into the
 * target week, locking any day that already has a booking in the target week.
 * `isAbsenceCodeBlocked` lets callers omit restricted codes for the current user.
 */
export function buildCopyRows(sourceWeek: Week, targetWeek: Week, isAbsenceCodeBlocked: (jiraId: string) => boolean): BuildCopyRowsResult {
    const sourceMap = mapWeekRows(sourceWeek);
    const targetMap = mapWeekRows(targetWeek, true);
    const rows: EditableRow[] = [];
    const blockedJiraIds: string[] = [];

    for (const [jiraId, sourceRow] of Array.from(sourceMap.entries())) {
        if (isAbsenceCodeBlocked(jiraId)) {
            if (!blockedJiraIds.includes(jiraId)) { blockedJiraIds.push(jiraId); }
            continue;
        }
        // Performance incentive (overtime) entries are never carried over by Copy Week.
        const sourceHours = sourceRow.hours.map((h, day) => (sourceRow.overtimeDays?.[day] ? 0 : h));

        if (targetMap.has(jiraId)) {
            const targetRow = targetMap.get(jiraId) as EditableRow;
            const lockedDays = targetRow.hours.map((hours) => hours > 0);
            const hours = targetRow.hours.map((targetHours, day) => (lockedDays[day] ? targetHours : sourceHours[day]));
            if (!lockedDays.some(Boolean) && !hours.some((h) => h > 0)) { continue; }
            rows.push({
                jiraId,
                summary: targetRow.summary || sourceRow.summary,
                hours,
                approvedDays: [...targetRow.approvedDays],
                locked: lockedDays.some(Boolean),
                lockedDays,
                sourceExistsInTarget: true,
                fromApi: true
            });
        } else {
            if (!sourceHours.some((h) => h > 0)) { continue; }
            rows.push({
                jiraId,
                summary: sourceRow.summary,
                hours: [...sourceHours],
                approvedDays: [...sourceRow.approvedDays],
                locked: false,
                lockedDays: [false, false, false, false, false, false, false],
                sourceExistsInTarget: false,
                fromApi: true
            });
        }
    }

    rows.sort((a, b) => a.jiraId.localeCompare(b.jiraId));
    return {rows, blockedJiraIds};
}

/** Builds the editable row set for Edit Week: current week's real values, flagged when restricted. */
export function buildEditRows(week: Week, isAbsenceCodeBlocked: (jiraId: string) => boolean): EditableRow[] {
    const rows = Array.from(mapWeekRows(week, true).values()).map((row) => ({
        ...row,
        hours: [...row.hours],
        approvedDays: [...row.approvedDays],
        locked: false,
        lockedDays: [false, false, false, false, false, false, false] as boolean[],
        blocked: isAbsenceCodeBlocked(row.jiraId),
        sourceExistsInTarget: false,
        fromApi: true
    }));
    rows.sort((a, b) => a.jiraId.localeCompare(b.jiraId));
    return rows;
}

/** Flattens a timetable response into a `jiraId|date -> positive hours` map (for delta computation). */
export function extractHoursByJiraAndDate(week: Week): Map<string, number> {
    const result = new Map<string, number>();
    const weekDates = week.week_dates;
    const bookings = week.bookings;

    for (const jiraId of Object.keys(bookings)) {
        const booking = bookings[jiraId] || ({} as Week["bookings"][string]);
        const hours = Array.isArray(booking.story_booking_hours) ? booking.story_booking_hours : [];

        for (let i = 0; i < weekDates.length; i += 1) {
            const amount = parseRawAmount(hours[i]);
            if (amount <= 0) { continue; }
            result.set(`${jiraId}|${weekDates[i]}`, amount);
        }
    }

    return result;
}

/** True when the target week already has a non-approved booking for jiraId on dayIdx (conflict guard). */
export function hasNonApprovedOnDay(targetWeek: Week, jiraId: string, dayIdx: number): boolean {
    const booking = (targetWeek.bookings && targetWeek.bookings[jiraId]) || ({} as Week["bookings"][string]);
    const idsByDay = Array.isArray(booking.db_booking_ids) ? booking.db_booking_ids : [];
    const statusByDay = Array.isArray(booking.status) ? booking.status : [];
    const idCell = String(idsByDay[dayIdx] || "");
    const bookingIds = idCell.split("+").map((v) => v.trim()).filter(Boolean);
    if (bookingIds.length === 0) { return false; }
    return !isApprovedStatus(statusByDay[dayIdx]);
}

export interface WeekOverviewSummary {
    entryCount: number;
    totalHours: number;
    weekdayHours: number;
    dayTotals: number[];
    rows: {jiraId: string; summary: string; hours: number[]; rowSum: number}[];
}

/** Read-only per-JIRA summary of a week, used for the Copy Week source-week preview. */
export function summarizeWeekForOverview(week: Week): WeekOverviewSummary {
    const rows = Array.from(mapWeekRows(week).values());
    const dayTotals = [0, 0, 0, 0, 0, 0, 0];
    let totalHours = 0;

    const ranked = rows.map((row) => {
        const rowSum = row.hours.reduce((sum, h, day) => {
            dayTotals[day] += h;
            return sum + h;
        }, 0);
        totalHours += rowSum;
        return {jiraId: row.jiraId, summary: row.summary, hours: [...row.hours], rowSum};
    });

    ranked.sort((a, b) => b.rowSum - a.rowSum || a.jiraId.localeCompare(b.jiraId));

    return {
        entryCount: ranked.length,
        totalHours,
        weekdayHours: dayTotals.slice(0, 5).reduce((sum, h) => sum + h, 0),
        dayTotals,
        rows: ranked
    };
}

export interface ResultRow {
    jiraId: string;
    summary: string;
    comment: string;
    hours: number[];
    lockedDays: boolean[];
    fromApi: boolean;
}

export interface WeekActionConflict {
    jiraId: string;
    day: string;
    date: string;
    summary: string;
    comment: string;
}

export interface WorklogAction {
    jiraId: string;
    date: string;
    hours: number;
    comment: string;
}

export interface BuildCopyActionsResult {
    actions: WorklogAction[];
    conflicts: WeekActionConflict[];
}

/** Turns reviewed Copy-Week rows into worklog/save actions, flagging conflicts on favorite-added rows. */
export function buildCopyActions(rows: ResultRow[], targetWeek: Week, targetDates: string[]): BuildCopyActionsResult {
    const actions: WorklogAction[] = [];
    const conflicts: WeekActionConflict[] = [];

    for (const row of rows) {
        for (let day = 0; day < 7; day += 1) {
            if (row.lockedDays[day]) { continue; }
            const value = row.hours[day];
            if (value <= 0) { continue; }
            if (row.fromApi === false && hasNonApprovedOnDay(targetWeek, row.jiraId, day)) {
                conflicts.push({jiraId: row.jiraId, day: WEEKDAY_NAMES[day], date: targetDates[day], summary: row.summary, comment: row.comment});
                continue;
            }
            actions.push({jiraId: row.jiraId, date: targetDates[day], hours: value, comment: row.comment});
        }
    }

    return {actions, conflicts};
}

export interface ReductionAction {
    bookingId: string;
    status: string;
    newValue: number;
}

export interface BuildEditActionsResult {
    increaseActions: WorklogAction[];
    reductionActions: ReductionAction[];
    conflicts: WeekActionConflict[];
    skippedApprovedChanges: number;
}

/** Turns reviewed Edit-Week rows into worklog/save (increase) and worklog/update (reduce-to-0) actions. */
export function buildEditActions(rows: ResultRow[], targetWeek: Week, targetDates: string[], originalMap: Map<string, number>): BuildEditActionsResult {
    const increaseActions: WorklogAction[] = [];
    const reductionActions: ReductionAction[] = [];
    const conflicts: WeekActionConflict[] = [];
    let skippedApprovedChanges = 0;

    for (const row of rows) {
        const booking = (targetWeek.bookings && targetWeek.bookings[row.jiraId]) || ({} as Week["bookings"][string]);
        const idsByDay = Array.isArray(booking.db_booking_ids) ? booking.db_booking_ids : [];
        const statusByDay = Array.isArray(booking.status) ? booking.status : [];

        if (row.fromApi === false) {
            for (let day = 0; day < 7; day += 1) {
                const date = targetDates[day];
                const newValue = row.hours[day];
                if (newValue <= 0) { continue; }
                if (hasNonApprovedOnDay(targetWeek, row.jiraId, day)) {
                    conflicts.push({jiraId: row.jiraId, day: WEEKDAY_NAMES[day], date, summary: row.summary, comment: row.comment});
                    continue;
                }
                increaseActions.push({jiraId: row.jiraId, date, hours: newValue, comment: row.comment});
            }
            continue;
        }

        for (let day = 0; day < 7; day += 1) {
            const date = targetDates[day];
            const key = `${row.jiraId}|${date}`;
            const currentValue = originalMap.get(key) || 0;
            const newValue = row.hours[day];
            const delta = newValue - currentValue;
            const status = String(statusByDay[day] || "");

            if (isApprovedStatus(status)) {
                if (delta !== 0) { skippedApprovedChanges += 1; }
                continue;
            }

            if (delta > 0) {
                increaseActions.push({jiraId: row.jiraId, date, hours: delta, comment: row.comment});
            } else if (delta < 0) {
                const idCell = String(idsByDay[day] || "");
                const bookingIds = idCell.split("+").map((v) => v.trim()).filter(Boolean);
                if (bookingIds.length > 0) {
                    reductionActions.push({bookingId: bookingIds[0], status, newValue});
                    for (let i = 1; i < bookingIds.length; i += 1) {
                        reductionActions.push({bookingId: bookingIds[i], status, newValue: 0});
                    }
                }
            }
        }
    }

    return {increaseActions, reductionActions, conflicts, skippedApprovedChanges};
}
